import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { IEventoGenerico, IPopulate, IQueryParam } from 'modelos/src';
import { HelperService } from '../../../../../../auxiliares/servicios/helper.service';
import { Subscription } from 'rxjs';
import { IFiltroTabla } from '../../../../../../auxiliares/tabla/filtro/filtro.component';
import { IColumnas } from '../../../../../../auxiliares/tabla/tabla.component';
import { MatDialog } from '@angular/material/dialog';
import { TratamientoGenericoComponent } from '../../../../eventos-genericos/tratar-evento-generico/tratamiento-generico/tratamiento-generico.component';
import { Lista2Service } from '../../../../../../auxiliares/servicios/lista2.service';
import { ClienteJerarquiaService } from '../../../../../../auxiliares/servicios/cliente-jerarquia.service';
import { estadoHtml } from '../../../../../../auxiliares/tabla/celdas';

@Component({
  selector: 'app-eventos-tecnicos-finalizados-genericos',
  templateUrl: './eventos-tecnicos-finalizados.component.html',
  styleUrl: './eventos-tecnicos-finalizados.component.scss',
  standalone: false,
})
export class EventosTecnicosFinalizadosGenericosComponent
  implements OnInit, OnDestroy
{
  helper = inject(HelperService);
  private clienteJerarquia = inject(ClienteJerarquiaService);
  private lista2 = inject(Lista2Service);
  private dialog = inject(MatDialog);

  //// Tabla nueva
  public name = 'EventosTecnicosFinalizadosComponent';
  public datos: IEventoGenerico[] = [];
  public datos$?: Subscription;
  public totalCount = 0;
  public columnas?: IColumnas<IEventoGenerico>[];

  // Filtros Tabla
  private categoria: IFiltroTabla = {
    elementos: [
      'Alarma',
      'Tracker',
      'Luminaria',
      'Sirena',
      'Gateway',
      'Vehículo',
    ],
    filter: {
      field: 'valores.categoria',
    },
    label: 'Categoría',
    tipo: 'select',
  };
  private fecha: IFiltroTabla = {
    filter: {
      field: 'fechaCreacion',
    },
    label: 'Fecha',
    tipo: 'dateRange',
  };
  private cliente: IFiltroTabla = {
    filter: {
      field: 'idCliente',
    },
    label: 'Cliente',
    tipo: 'cliente-selector',
  };
  // Cliente en cuya jerarquía cuelga la entidad: el cliente elegido y toda su
  // descendencia.
  private ancestro: IFiltroTabla = {
    filter: {
      field: 'idsAncestros',
      incluirPropio: true,
    },
    label: 'En la jerarquía de',
    tipo: 'cliente-selector',
  };

  public filtros: IFiltroTabla[] = [
    this.categoria,
    this.fecha,
    this.cliente,
    this.ancestro,
  ];

  public populate: IPopulate[] = [
    {
      path: 'cliente',
      select: 'nombre',
    },
    {
      path: 'activo',
      select: 'identificacion',
    },
    {
      path: 'alarma',
      populate: [
        {
          path: 'domicilio',
          select: 'direccion identificacion geojson',
        },
        {
          path: 'comunicador',
          select: 'marca modelo',
        },
        {
          path: 'modelo',
          select: 'marca modelo',
        },
      ],
    },
    {
      path: 'tracker',
      select: 'identificacion',
    },
    {
      path: 'luminaria',
      select: 'identificacion',
    },
    {
      path: 'gateway',
      select: 'nombre',
    },
    {
      path: 'usuario',
      select: 'usuario datosPersonales',
    },
    {
      path: 'detallesTecnicos.tecnico',
      select: 'usuario datosPersonales',
    },
  ];
  public query: IQueryParam = {
    populate: JSON.stringify(this.populate),
    sort: '-fechaCreacion',
    page: 0,
    limit: +this.helper.getPageSize(this.name),
  };

  public async listar(): Promise<void> {
    // Finalizados = eventos 'Servicio Técnico' finalizados. El filtro fijo se
    // mergea en cada listar() para preservar los filtros del usuario.
    const filterObj: Record<string, unknown> = HelperService.stringToObject(
      this.query.filter,
    );
    filterObj['filtrador'] = 'Servicio Técnico';
    filterObj['estado'] = 'Finalizado';
    const queryToSend: IQueryParam = {
      ...this.query,
      filter: JSON.stringify(filterObj),
    };

    this.datos$?.unsubscribe();
    this.datos$ = this.lista2
      .eventosGenericos(queryToSend, 30)
      .subscribe((data) => {
        this.totalCount = data.totalCount;
        this.datos = data.datos;
      });
  }

  // Acciones
  public async atender(data: IEventoGenerico) {
    this.dialog.open(TratamientoGenericoComponent, {
      data: { idEvento: data._id },
      hasBackdrop: false,
      minWidth: '1100px',
      maxWidth: '1500px',
    });
  }

  // Tabla

  private setColumnas() {
    const columnas: IColumnas<IEventoGenerico>[] = [
      // Fecha
      {
        header: {
          label: 'Fecha',
          sortable: true,
          sort: 'fechaCreacion',
        },
        row: {
          fecha: (dato) => dato.fechaCreacion,
          hace: true,
          noData: 'Sin fecha',
        },
      },
      // Categoría
      {
        header: {
          label: 'Categoría',
        },
        row: {
          html: (dato) => {
            const categoria = (dato.valores as any)?.categoria;
            if (categoria) {
              // Color del cliente: badge de ese color con el nombre adentro
              return estadoHtml({
                texto: categoria,
                tono: 'neutro',
                color: dato.valores?.color,
              });
            }
            return '';
          },
          noData: 'Sin categoría',
        },
      },
      // Título
      {
        header: {
          label: 'Título',
          sortable: true,
          sort: 'valores.titulo',
        },
        row: {
          parse(dato) {
            return dato.valores?.titulo;
          },
          noData: 'Sin título',
        },
      },
      // Elemento
      {
        header: {
          label: 'Elemento',
        },
        row: {
          parse(dato) {
            if (dato?.tracker) {
              return dato.tracker?.identificacion;
            }
            if (dato?.alarma) {
              return dato.alarma?.nombre;
            }
            if (dato?.activo) {
              return dato.activo?.identificacion;
            }
            if (dato?.luminaria) {
              return dato.luminaria?.identificacion;
            }
            if (dato?.gateway) {
              return dato.gateway?.nombre;
            }
            return undefined;
          },
          noData: 'Sin elemento',
        },
      },
      // Usuario
      {
        header: {
          label: 'Usuario',
        },
        row: {
          parse(dato) {
            if (dato.usuario?.usuario || dato.usuario?.datosPersonales) {
              return (
                dato.usuario?.datosPersonales?.nombre || dato.usuario?.usuario
              );
            }
            return undefined;
          },
          noData: 'Sin usuario',
        },
      },

      // Tecnico
      {
        header: {
          label: 'Técnico',
        },
        row: {
          parse(dato) {
            const tecnico = dato.detallesTecnicos?.tecnico;
            if (tecnico?.usuario || tecnico?.datosPersonales) {
              return tecnico?.datosPersonales?.nombre || tecnico?.usuario;
            }
            return undefined;
          },
          noData: 'Sin técnico asignado',
        },
      },
    ];

    // Cliente
    if (HelperService.getTipoCliente() !== 'Final') {
      {
        columnas.push({
          header: {
            label: 'Cliente',
          },
          row: {
            html: (dato) => this.clienteJerarquia.htmlNombre(dato.cliente),
            tooltip: (dato) =>
              this.clienteJerarquia.tooltipJerarquia(dato.cliente),
            noData: 'Sin cliente',
          },
        });
      }
    }

    // Acciones
    columnas.push({
      header: {
        label: 'Acciones',
      },
      row: {
        acciones: [
          {
            tipo: 'material',
            icon: 'visibility',
            tooltip: 'Ver detalle',
            click: (dato) => this.atender(dato),
            disabled: () =>
              !this.helper.tienePermiso(
                'Servicio Técnico - Atender solicitudes',
              ),
          },
        ],
      },
    });

    this.columnas = columnas;
  }

  // Hooks

  async ngOnInit() {
    this.setColumnas();
    await this.listar();
  }

  ngOnDestroy() {
    this.datos$?.unsubscribe();
  }
}
